import {
  BillingItem,
  CustomSubscriptionType,
  INVOICE_TABLE_NAME,
  getTypedCustomSubscriptionValueForRefetch,
  onCreateCustomSubscription,
  onDeleteCustomSubscription,
  onUpdateCustomSubscription,
  type CustomSubscription,
  type OnCreateCustomSubscriptionSubscription,
  type OnDeleteCustomSubscriptionSubscription,
  type OnUpdateCustomSubscriptionSubscription,
  CreateBillingItemInput,
  CreateBillingItemMutationVariables,
  DeleteBillingItemMutationVariables,
  OnCreateBillingItemSubscription,
  OnDeleteBillingItemSubscription,
  OnUpdateBillingItemSubscription,
  UpdateBillingItemInput,
  cleanInputCreate,
  cleanInputUpdate,
  createBillingItem as createMutation,
  deleteBillingItem as deleteMutation,
  getBillingItem as getBillingItemQuery,
  getReadId,
  getTableClientId,
  isNilOrEmpty,
  onCreateBillingItem,
  onDeleteBillingItem,
  onUpdateBillingItem,
  syncBillingItems,
  updateBillingItem as updateMutation,
} from '@rentguru/commons-utils';
import { deleteAndHideEntityWithFetchBefore, list, mutation, useSubscriptions } from '@up2rent/graphql';
import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import { Reducer, createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import type { NormalizedBillingItem } from 'src/components/MyCompany/NewOwnerInvoice/Components/utils';

import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

const normalizeBillingItem = (item: BillingItem, ownerId?: string): NormalizedBillingItem => ({
  ...item,
  ownerId,
  baseAmount: item.baseAmount ?? undefined,
  billingType: item.billingType ?? undefined,
  amountCalculationType: item.amountCalculationType ?? undefined,
  percentageBase: item.percentageBase ?? undefined,
  period: item.period ?? undefined,
  vatRateId: item.vatRateId ?? undefined,
  vatRate: item.vatRate ?? undefined,
  invoiceId: item.invoiceId ?? undefined,
  quantity: item.quantity ?? undefined,
  percentage: item.percentage ?? undefined,
  allocationOption: item.allocationOption ?? undefined,
  multipleUnitsSplit: item.multipleUnitsSplit ?? undefined,
  leaseIds: item.leaseIds?.flatMap((id) => (id !== null ? [id] : [])) ?? undefined,
  unitIds: item.unitIds?.flatMap((id) => (id !== null ? [id] : [])) ?? undefined,
  buildingIds: item.buildingIds?.flatMap((id) => (id !== null ? [id] : [])) ?? undefined,
  accountLabelId: item.accountLabelId ?? undefined,
  _deleted: item._deleted ?? undefined,
});

export interface BillingItemsContext extends BillingItemsState {
  getBillingItem: (id: string) => NormalizedBillingItem | undefined;
  getBillingItemsByInvoice: (invoiceId: string) => NormalizedBillingItem[];
  createBillingItem: (input: Omit<CreateBillingItemInput, 'clientId' | 'readId'>) => Promise<BillingItem>;
  updateBillingItem: (updates: UpdateBillingItemInput) => Promise<BillingItem>;
  deleteBillingItem: (billingItem: NormalizedBillingItem) => Promise<BillingItem | null>;
  refetchBillingItems: () => Promise<void>;
}

interface BillingItemsState {
  billingItems: NormalizedBillingItem[] | null;
  templateBillingItems: NormalizedBillingItem[] | null;
  billingItemsFetched: boolean;
  loading: boolean;
  error: string | null;
}

type Action =
  | { type: 'LOADING' }
  | { type: 'ERROR'; payload: { error: string } }
  | {
      type: 'ADD' | 'UPDATE';
      payload: { billingItem: NormalizedBillingItem };
    }
  | {
      type: 'FETCHED';
      payload: { billingItems: NormalizedBillingItem[]; templateBillingItems: NormalizedBillingItem[] };
    }
  | { type: 'DELETE'; payload: { id: string } };

const initialState: BillingItemsState = {
  billingItems: null,
  templateBillingItems: null,
  billingItemsFetched: false,
  loading: false,
  error: null,
};

const billingItemsReducer = (state: BillingItemsState, action: Action): BillingItemsState => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: true, error: null };
    case 'ERROR':
      return { ...state, loading: false, error: action.payload.error };
    case 'FETCHED':
      return {
        ...state,
        loading: false,
        error: null,
        billingItemsFetched: true,
        billingItems: action.payload.billingItems,
        templateBillingItems: action.payload.templateBillingItems,
      };
    case 'ADD': {
      const normalized = action.payload.billingItem;
      return {
        ...state,
        billingItems: [...(state.billingItems ?? []), normalized],
        templateBillingItems: isNilOrEmpty(normalized.invoiceId)
          ? [...(state.templateBillingItems ?? []), normalized]
          : state.templateBillingItems,
      };
    }
    case 'UPDATE': {
      const normalized = action.payload.billingItem;
      return {
        ...state,
        billingItems: (state.billingItems ?? []).map((item) => (item.id === normalized.id ? normalized : item)),
        templateBillingItems: (state.templateBillingItems ?? []).map((item) =>
          item.id === normalized.id ? normalized : item
        ),
      };
    }
    case 'DELETE':
      return {
        ...state,
        billingItems: (state.billingItems ?? []).filter((item) => item.id !== action.payload.id),
        templateBillingItems: (state.templateBillingItems ?? []).filter((item) => item.id !== action.payload.id),
      };
    default:
      return state;
  }
};

export const BillingItemsContext = createContext<BillingItemsContext | null>(null);

const REFETCH_BURST_DEBOUNCE_IN_MS = 2000;

export const BillingItemsContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const [state, dispatch] = useReducer<Reducer<BillingItemsState, Action>>(billingItemsReducer, initialState);
  const { clientId } = useUser();
  const { agencyRateDelete } = usePermissions();

  const refetchBillingItems = async () => {
    dispatch({ type: 'LOADING' });
    try {
      const result = await list<BillingItem>(syncBillingItems, 'clientId', getTableClientId(clientId!, 'BillingItem'));
      const all = result.map((item) => normalizeBillingItem(item, clientId!));
      dispatch({
        type: 'FETCHED',
        payload: {
          billingItems: all,
          templateBillingItems: all.filter((item) => isNilOrEmpty(item.invoiceId)),
        },
      });
    } catch (error) {
      dispatch({ type: 'ERROR', payload: { error: (error as Error)?.message ?? String(error) } });
      throw error;
    }
  };

  const refetchBillingItemsRef = useRef(refetchBillingItems);
  refetchBillingItemsRef.current = refetchBillingItems;
  const debouncedRefetchBillingItems = useMemo<DebouncedFunc<() => void>>(
    () =>
      debounce(() => {
        refetchBillingItemsRef.current().catch(() => {});
      }, REFETCH_BURST_DEBOUNCE_IN_MS),
    []
  );
  useEffect(() => () => debouncedRefetchBillingItems.cancel(), [debouncedRefetchBillingItems]);

  useEffect(() => {
    if (clientId) {
      refetchBillingItems().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useSubscriptions<OnCreateBillingItemSubscription, OnUpdateBillingItemSubscription, OnDeleteBillingItemSubscription>(
    onCreateBillingItem,
    onUpdateBillingItem,
    onDeleteBillingItem,
    (data) => {
      const billingItem = data.onCreateBillingItem as BillingItem;
      dispatch({ type: 'ADD', payload: { billingItem: normalizeBillingItem(billingItem) } });
    },
    (data) => {
      dispatch({
        type: 'UPDATE',
        payload: { billingItem: normalizeBillingItem(data.onUpdateBillingItem as BillingItem) },
      });
    },
    (data) => {
      const { id } = data.onDeleteBillingItem as BillingItem;
      dispatch({ type: 'DELETE', payload: { id } });
    }
  );

  // Backend flows write billing items straight to DynamoDB (no subscription) and announce them with this refetch.
  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (newData.name !== CustomSubscriptionType.REFETCH || !clientId) {
        return;
      }
      const typedValue = getTypedCustomSubscriptionValueForRefetch(newData);
      if (typedValue.table === INVOICE_TABLE_NAME && typedValue.isCompanyInvoice) {
        debouncedRefetchBillingItems();
      }
    },
    () => {},
    () => {}
  );

  const getBillingItem = (id: string) => (state.billingItems ?? []).find((billingItem) => billingItem.id === id);

  const getBillingItemsByInvoice = (invoiceId: string) => {
    const billingItems = state.billingItems ?? [];
    return billingItems.filter((item) => item.invoiceId === invoiceId);
  };

  const createBillingItem = async (
    input: Omit<CreateBillingItemInput, 'clientId' | 'readId'>
  ): Promise<BillingItem> => {
    return await mutation<BillingItem, CreateBillingItemMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateBillingItemInput),
        clientId: getTableClientId(clientId!, 'BillingItem'),
        readId: getReadId(clientId!, 'BillingItem'),
      },
    });
  };

  const updateBillingItem = async (updates: UpdateBillingItemInput): Promise<BillingItem> => {
    return await mutation<BillingItem>(updateMutation, {
      input: cleanInputUpdate(updates, false),
    });
  };

  const deleteBillingItem = async (billingItem: NormalizedBillingItem): Promise<BillingItem | null> => {
    if (!agencyRateDelete) return null;
    return await deleteAndHideEntityWithFetchBefore<BillingItem, DeleteBillingItemMutationVariables>(
      billingItem as BillingItem,
      getBillingItemQuery,
      deleteMutation
    );
  };

  const values = useMemo(
    () => ({
      ...state,
      getBillingItem,
      getBillingItemsByInvoice,
      createBillingItem,
      updateBillingItem,
      deleteBillingItem,
      refetchBillingItems,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <BillingItemsContext.Provider value={values}>{children}</BillingItemsContext.Provider>;
};

export const useBillingItems = (): BillingItemsContext => {
  const context = useContext<BillingItemsContext | null>(BillingItemsContext);

  if (isNil(context)) {
    throw new Error('`useBillingItems` hook must be used within a `BillingItemsContextProvider` component');
  }

  return context;
};
